import { useCallback, useEffect, useState } from 'react'
import type { Project } from '#shared/projects'
import { api, type TagWithUsage } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { NewProjectForm } from '../components/NewProjectForm'
import { ProjectManager } from '../components/ProjectManager'
import { TagsCard } from '../components/TagsCard'

export function Projects() {
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [tags, setTags] = useState<TagWithUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isManager = user?.role === 'manager'

  // Archived included in both so a manager can see and restore everything.
  const load = useCallback(async () => {
    try {
      const [nextProjects, nextTags] = await Promise.all([
        api.listProjects(true),
        api.listTagsWithUsage(),
      ])
      setProjects(nextProjects)
      setTags(nextTags)
      setError(null)
    } catch {
      setError('Could not load projects and tags.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section>
      <h1>Projects</h1>
      <p className="muted">
        Time is logged against one project, with any number of tags — or none, and
        none at all on a project that does not take them.
        {isManager ? ' Both lists are yours to maintain.' : ''}
      </p>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {isManager && <NewProjectForm onCreated={load} />}

      {!loading && (
        <>
          <ProjectManager projects={projects} canManage={isManager} onChanged={load} />
          <TagsCard tags={tags} canManage={isManager} onChanged={load} />
        </>
      )}
    </section>
  )
}
